import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from 'src/app/services/user.service';
import { Subscription } from 'rxjs';
import { EventService } from 'src/app/services/event.service';
import Event from 'src/app/interfaces/Event';
import { Location } from '@angular/common';
import { ToastService } from 'src/app/services/toast.service';
import { UploadService } from 'src/app/services/upload.service';
import { LoadingService } from 'src/app/services/loading.service';
import { NotificationsService } from 'src/app/services/notifications.service';
import * as moment from 'moment-timezone';
import { UrlService } from 'src/app/services/url.service';

@Component({
  selector: 'app-edit-event',
  templateUrl: './edit-event.component.html',
  styleUrls: ['./edit-event.component.scss'],
})
export class EditEventComponent implements OnInit, OnDestroy {
  isNewEvent = false;
  step = 0;
  subscriptions: Subscription = new Subscription();
  currentUser;
  event: Event = {
    image: null,
    owner: null,
    ownerMeta: {
      name: null,
    },
    id: null,
    title: null,
    timestamp: null,
    description: null,
    dateTime: {
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
      timezone: {
        name: null,
        abbr: null,
      },
    },
    location: {
      street: null,
      city: null,
      zip: null,
      country: null,
      remote: false,
      remoteLink: null,
    },
    externalLink: null,
    archived: false,
    autoArchiveOverride: false,
  };
  editorModules = {
    toolbar: [['link', 'bold', 'italic', 'underline', 'strike', 'blockquote', { list: 'ordered' }, { list: 'bullet' }]],
  };
  timezones;
  userTimezone;
  userTimezoneAbbr;

  previewFile;
  attachmentFile;

  startDateErrorMessage = '';
  endDateErrorMessage = '';
  startTimeErrorMessage = '';
  endTimeErrorMessage = '';

  isSafari = false;

  constructor(
    private route: ActivatedRoute,
    private userService: UserService,
    private eventService: EventService,
    private location: Location,
    private router: Router,
    private renderer: Renderer2,
    private toastService: ToastService,
    private uploadService: UploadService,
    private loadingService: LoadingService,
    private notificationService: NotificationsService,
    private urlService: UrlService
  ) {}

  ngOnInit() {
    // Set timezone data
    this.timezones = moment.tz.names().filter((name, i) => {
      return name.toLowerCase().startsWith('africa') ||
        name.toLowerCase().startsWith('america') ||
        name.toLowerCase().startsWith('asia') ||
        name.toLowerCase().startsWith('australia') ||
        name.toLowerCase().startsWith('atlantic') ||
        name.toLowerCase().startsWith('europe') ||
        name.toLowerCase().startsWith('indian') ||
        name.toLowerCase().startsWith('pacific') 
    }).sort((a,b) => {
      const formattedValues = [a,b].map((name) => {
        const utc = moment.tz(moment(), name).format('Z');
        const negative = utc.startsWith('-');
        const val = parseInt(utc.substring(1).replace(':', ''));
        return negative ? 0 - val : val;
      })
      return formattedValues[0] - formattedValues[1];
    }).map((name) => {
      const utc = moment.tz(moment(), name).format('Z');
      return {
        value: name,
        label: `(UTC${utc}) ${name}`,
      };
    });
    if (!this.event.dateTime.timezone.name && !this.event.dateTime.timezone.abbr && moment.tz.guess()) {
      this.userTimezone = moment.tz.guess();
      this.userTimezoneAbbr = moment.tz(moment(), this.userTimezone).zoneAbbr();
      this.event.dateTime.timezone.name = this.userTimezone;
      this.event.dateTime.timezone.abbr = this.userTimezoneAbbr;
    }

    // See if Safari
    var ua = navigator.userAgent.toLowerCase(); 
    if (ua.indexOf('safari') != -1) { 
      if (ua.indexOf('chrome') == -1) {
        this.isSafari = true;
      }
    }

    // See if new event
    this.route.queryParams.subscribe((params) => {
      if (params['new']) {
        this.isNewEvent = params['new'];
      } else {
        if (params['id']) {
          this.eventService.getCurrentEvent(params['id']).subscribe((e) => {
            this.event = e[0];
          });
        }
      }
    });

    // Get user
    this.subscriptions.add(
      this.userService.user.subscribe((u) => {
        if (u && u.uid) {
          this.currentUser = u;
        }
      })
    );
  }
  exitModal() {
    if (this.attachmentFile) {
      this.uploadService.uploadFile(this.attachmentFile).then((attachmentUrl) => {
        this.event.image = attachmentUrl;
        this.saveEvent();
      });
    } else {
      this.saveEvent();
    }
  }

  saveEvent() {
    this.loadingService.startLoading();
    if (this.isNewEvent) {
      this.event.owner = this.currentUser.uid;
      this.event.ownerMeta.name = this.currentUser.displayName;
      this.event.timestamp = new Date();
      this.event.dateTime.startTime = this.convertTime(this.event.dateTime.startTime);
      this.event.dateTime.endTime = this.event.dateTime.endTime ? this.convertTime(this.event.dateTime.endTime) : null;
      this.event.dateTime.endDate = this.event.dateTime.endDate ? this.event.dateTime.endDate : this.event.dateTime.startDate;

      this.event.description = this.urlService.processContent(this.event.description);

      this.eventService.createEvent(this.event).then(
        (id: string) => {
          this.notificationService.sendNotifications({
            type: 'newEvents',
            author: this.currentUser.uid,
            title: this.event.title,
            url: `/events/${id}`,
          });
          this.loadingService.stopLoading();
          // this.router.navigateByUrl(`/events/${id}`);
          this.router.navigate(['/events/' + id]);
        },
        (e) => console.error(e)
      );
    } else {
      this.event.description = this.urlService.processContent(this.event.description);
      this.eventService.updateEvent(this.event.id, this.event).then(
        (res) => {
          this.loadingService.stopLoading();
          // this.router.navigateByUrl(`/events/${this.event.id}`);
          this.router.navigate(['/events/' + this.event.id]);
        },
        (err) => {
          console.error(err);
        }
      );
    }
  }

  onFileChanged(event) {
    if (event.target.files && event.target.files[0]) {
      var reader = new FileReader();
      var t = this;
      reader.onload = function (e) {
        t.previewFile = reader.result;
      };

      reader.readAsDataURL(event.target.files[0]); // convert to base64 string

      this.attachmentFile = event.target.files[0];
    }
  }
  removePhoto() {
    this.previewFile = null;
    this.attachmentFile = null;
    this.event.image = null;
  }

  checkDate(field, startOrEnd) {
    if (!this.isSafari) return;

    var allowBlank = true;
    var minYear = (new Date()).getFullYear();
    var maxYear = 2100;

    var errorMsg = "";

    // regular expression to match required date format
    var re = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

    if(field != '') {
      var regs = field.match(re);
      if(regs) {
        if(regs[2] < 1 || regs[2] > 31) {
          errorMsg = "Invalid value for day: " + regs[1];
        } else if(regs[1] < 1 || regs[1] > 12) {
          errorMsg = "Invalid value for month: " + regs[2];
        } else if(regs[3] < minYear || regs[3] > maxYear) {
          errorMsg = "Invalid value for year: " + regs[3] + " - must be between " + minYear + " and " + maxYear;
        }
      } else {
        errorMsg = "Invalid date format: " + field;
      }
    } else if(!allowBlank) {
      errorMsg = "Empty date not allowed!";
    }

    if(errorMsg != "") {
      this[startOrEnd + 'DateErrorMessage'] = errorMsg;
    } else {
      this[startOrEnd + 'DateErrorMessage'] = '';
    }
  }

  checkTime(field, startOrEnd) {
    var errorMsg = "";

    // regular expression to match required time format
    var re = /^(\d{1,2}):(\d{2})(:00)?\s([ap]m)?$/;

    if(field != "") {
      var regs = field.toLowerCase().match(re)
      if(regs) {
        if(regs[4]) {
          // 12-hour time format with am/pm
          if(regs[1] < 1 || regs[1] > 12) {
            errorMsg = "Invalid value for hours: " + regs[1];
          }
        } else {
          // 24-hour time format
          if(regs[1] > 23) {
            errorMsg = "Invalid value for hours: " + regs[1];
          }
        }
        if(!errorMsg && regs[2] > 59) {
          errorMsg = "Invalid value for minutes: " + regs[2];
        }
      } else {
        errorMsg = "Invalid time format: " + field;
      }
    }

    if(errorMsg != "") {
      this[startOrEnd + 'TimeErrorMessage'] = errorMsg;
    } else {
      this[startOrEnd + 'TimeErrorMessage'] = '';
    }
  }

  convertTime(time) {
    if (time.toLowerCase().indexOf('am') > -1 || time.toLowerCase().indexOf('pm') > -1) {
      let period = time.toLowerCase().match(/(a|p)m/)[0];
      let periodIndex = time.toLowerCase().match(/(a|p)m/).index;
      let hour = period === 'pm' ? parseInt(time) + 12 : parseInt(time);
      return hour + time.substring(time.indexOf(':'), periodIndex).trim();
    } else {
      return time;
    }
  }

  updateAbbr() {
    this.event.dateTime.timezone.abbr = moment.tz(moment(), this.event.dateTime.timezone.name).zoneAbbr();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
